import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Share2, Copy, Mail, RefreshCw, Power, Loader2, MessageSquare } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/sports-betting/board/share`;
export const linkFor = (t, code) => `${process.env.REACT_APP_BACKEND_URL}/api/public/picks/link/${t}${code ? `?c=${code}` : ''}`;
export const OWNER = {};
const SHARE_API = `${process.env.REACT_APP_BACKEND_URL}/api/sports-betting/board/share`;
export const shareText = (d) => `${linkFor(d.token, d.code)}\n\n\n7-day code - ${d.code}`;
// Copy text that depends on a fresh server fetch. ClipboardItem(promise) keeps iPhone Safari's tap permission.
export const copyFresh = async (make) => {
  const get = () => axios.get(SHARE_API, OWNER).then((r) => r.data);
  if (window.ClipboardItem && navigator.clipboard?.write) {
    let data;
    const blob = get().then((d) => { data = d; return new Blob([make(d)], { type: 'text/plain' }); });
    await navigator.clipboard.write([new window.ClipboardItem({ 'text/plain': blob })]);
    return data;
  }
  const d = await get();
  await navigator.clipboard.writeText(make(d));
  return d;
};

// Owner-side controls: one live link that only shows this week's safe tickets.
export default function SharePicks() {
  const [open, setOpen] = useState(false);
  const [share, setShare] = useState({});
  const token = share.token;
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState('');
  useEffect(() => { if (open) axios.get(API, OWNER).then((r) => setShare(r.data)).catch(() => {}); }, [open]);

  const run = async (key, fn) => { setBusy(key); try { await fn(); } catch (e) { toast.error(e.response?.data?.detail || e.message); } setBusy(''); };
  const create = (rotate) => run(rotate ? 'rotate' : 'create', async () => {
    const r = await axios.post(`${API}${rotate ? '?rotate=true' : ''}`, null, OWNER);
    setShare(r.data);
    toast.success(rotate ? 'New link made — the old one no longer works' : 'Share link ready');
  });
  // Always pull the current code at tap time so a page left open past midnight never sends yesterday's code.
  const fresh = async () => { const r = await axios.get(API, OWNER); setShare(r.data); return r.data; };
  const textFor = shareText;
  const copy = async () => {
    try { const d = await copyFresh((x) => linkFor(x.token, x.code)); setShare(d); toast.success(`Link copied — they'll need today's code ${d.code} to open it`); } catch (_e) { toast.error('Copy failed — select the link and copy it'); }
  };
  const copyMsg = async () => {
    try { const d = await copyFresh(shareText); setShare(d); toast.success(`Copied link + "7-day code - ${d.code}" — paste into any text or email`); } catch (_e) { toast.error('Copy failed'); }
  };
  const nativeShare = async () => {
    try { const d = await fresh(); await navigator.share({ text: textFor(d) }); } catch (_e) { /* cancelled */ }
  };
  const openSms = async () => { const d = await fresh(); window.location.href = `sms:?&body=${encodeURIComponent(textFor(d))}`; };
  const openMail = async () => {
    const d = await fresh();
    window.location.href = `mailto:?subject=${encodeURIComponent(`Nick's Picks — 7-day code ${d.code}`)}&body=${encodeURIComponent(textFor(d))}`;
  };
  const send = () => run('email', async () => {
    const d = await fresh();
    await axios.post(`${API}/email`, { token: d.token, to: email, link: linkFor(d.token, d.code) }, OWNER);
    toast.success(`Link + today's code emailed to ${email}`); setEmail('');
  });
  const stop = () => run('stop', async () => { await axios.delete(API, OWNER); setShare({}); toast.success('Share link turned off'); });

  return (
    <div className="rounded-xl border border-zinc-800 bg-zinc-900/60 p-3" data-testid="share-picks">
      <button type="button" onClick={() => setOpen((o) => !o)} data-testid="share-picks-toggle"
        className="inline-flex items-center gap-1.5 text-sm font-bold text-amber-300 hover:text-amber-200">
        <Share2 className="w-4 h-4" />Share Nick's Picks picks
      </button>
      {open && (
        <div className="mt-2 space-y-2">
          <p className="text-[11px] text-zinc-500">People with the link + today's code see ONLY the NFL, College Football and NBA picks (safe tickets and game picks). Nothing else in the app. Each code works for 7 full days from when you send it.</p>
          {!token ? (
            <button type="button" onClick={() => create(false)} disabled={!!busy} data-testid="share-create"
              className="inline-flex items-center gap-1.5 rounded-lg bg-amber-500 px-3 py-1.5 text-xs font-bold text-black hover:bg-amber-400">
              {busy === 'create' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Share2 className="w-3.5 h-3.5" />}Create share link
            </button>
          ) : (
            <>
              <p className="text-xs text-zinc-300 flex items-center gap-2" data-testid="share-code">Today's code: <b className="font-mono text-base tracking-[0.25em] text-amber-300">{share.code}</b>
                <button type="button" data-testid="share-copy-code" onClick={() => copyFresh((d) => d.code).then((d) => { setShare(d); toast.success(`Current code ${d.code} copied`); }).catch(() => toast.error('Copy failed'))}
                  className="rounded border border-zinc-700 px-1.5 py-0.5 text-[10px] text-zinc-300 hover:text-white">Copy code</button></p>
              <p className="text-[10px] text-zinc-500">Whoever opens the link must type today's code. Use "Copy text" or "Text it" to send the link and the code together. Each code works for 7 days.</p>
              <div className="flex gap-2">
                <input readOnly value={linkFor(token, share.code)} onFocus={(e) => e.target.select()} data-testid="share-link"
                  className="min-w-0 flex-1 rounded-md border border-zinc-700 bg-zinc-950 px-2 py-1.5 text-[11px] text-zinc-200" />
                <button type="button" onClick={copy} data-testid="share-copy"
                  className="inline-flex items-center gap-1 rounded-md bg-amber-500 px-3 py-1.5 text-xs font-bold text-black hover:bg-amber-400"><Copy className="w-3.5 h-3.5" />Copy</button>
              </div>
              <div className="flex flex-wrap gap-2" data-testid="share-send-row">
                <button type="button" onClick={copyMsg} data-testid="share-copy-message"
                  className="inline-flex items-center gap-1 rounded-md border border-amber-500/60 px-3 py-1.5 text-xs font-bold text-amber-200 hover:bg-amber-500/10"><Copy className="w-3.5 h-3.5" />Copy text (link + 7-day code)</button>
                <button type="button" onClick={openSms} data-testid="share-sms"
                  className="inline-flex items-center gap-1 rounded-md bg-emerald-500 px-3 py-1.5 text-xs font-bold text-black hover:bg-emerald-400"><MessageSquare className="w-3.5 h-3.5" />Text it</button>
                <button type="button" onClick={openMail} data-testid="share-mailto"
                  className="inline-flex items-center gap-1 rounded-md border border-zinc-600 px-3 py-1.5 text-xs font-bold text-white hover:bg-zinc-800"><Mail className="w-3.5 h-3.5" />Email app</button>
                {typeof navigator !== 'undefined' && navigator.share && (
                  <button type="button" onClick={nativeShare} data-testid="share-native"
                    className="inline-flex items-center gap-1 rounded-md border border-zinc-600 px-3 py-1.5 text-xs font-bold text-white hover:bg-zinc-800"><Share2 className="w-3.5 h-3.5" />Share…</button>
                )}
              </div>
              <div className="flex gap-2">
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="their@email.com" data-testid="share-email-input"
                  className="min-w-0 flex-1 rounded-md border border-zinc-700 bg-zinc-950 px-2 py-1.5 text-xs text-white" />
                <button type="button" onClick={send} disabled={!email || !!busy} data-testid="share-email-send"
                  className="inline-flex items-center gap-1 rounded-md border border-zinc-600 px-3 py-1.5 text-xs font-bold text-white hover:bg-zinc-800 disabled:opacity-50">
                  {busy === 'email' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Mail className="w-3.5 h-3.5" />}Email it
                </button>
              </div>
              <div className="flex gap-3">
                <button type="button" onClick={() => create(true)} disabled={!!busy} data-testid="share-rotate" className="inline-flex items-center gap-1 text-[11px] text-zinc-400 hover:text-white"><RefreshCw className="w-3 h-3" />New link (old stops working)</button>
                <button type="button" onClick={stop} disabled={!!busy} data-testid="share-stop" className="inline-flex items-center gap-1 text-[11px] text-red-400 hover:text-red-300"><Power className="w-3 h-3" />Turn off link</button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
